import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import { Toaster } from './components/ui/sonner';
import { toast } from 'sonner';
import { PlatformSettingsProvider } from './contexts/PlatformSettingsContext';
import { runSessionMigration } from './lib/sessionMigration';
import './App.css';

// Run before any module touches localStorage (auth state, etc.)
runSessionMigration();

// Components
import LandingPage from './components/LandingPage';
import PricingPage from './components/PricingPage';
import LoginPage from './components/LoginPage';
import RegisterPage from './components/RegisterPage';
import Dashboard from './components/Dashboard';
import UserRegistration from './components/UserRegistration';
import EmailVerificationPending from './components/EmailVerificationPending';
import EmailVerified from './components/EmailVerified';
import VerifyEmail from './pages/VerifyEmail';
import ChangePasswordModal from './components/ChangePasswordModal';
import PaymentSuccess from './pages/PaymentSuccess';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import ExpenseReport from './components/ExpenseReport';
import InviteExpired from './components/InviteExpired';
import InviteCancelled from './pages/InviteCancelled';
import AuditShareLanding from './components/AuditShareLanding';
import UltraAdminLogin from './components/UltraAdminLogin';
import UltraAdminLayout from './components/UltraAdminLayout';
import UltraDashboard from './components/ultra-admin/UltraDashboard';
import UltraTenantManagement from './components/ultra-admin/UltraTenantManagementNew';
import UltraUserManagement from './components/ultra-admin/UltraUserManagementNew';
import UltraAdminProfile from './components/ultra-admin/UltraAdminProfile';
import ImpersonationLogs from './components/ultra-admin/ImpersonationLogs';
import UltraBilling from './components/ultra-admin/UltraBilling';
import UltraPromoCodes from './components/ultra-admin/UltraPromoCodes';
import UltraAnalytics from './components/ultra-admin/UltraAnalytics';
import UltraFeatureManagement from './components/ultra-admin/UltraFeatureManagement';
import UltraPlanComparison from './components/ultra-admin/UltraPlanComparison';
import UltraStorageUsage from './components/ultra-admin/UltraStorageUsage';
import UltraRoadmap from './components/ultra-admin/UltraRoadmap';
import UltraAuditLogs from './components/ultra-admin/UltraAuditLogs';
import UltraPlatformSettings from './components/ultra-admin/UltraPlatformSettings';
import CurrencyLibrary from './components/CurrencyLibrary';
import UltraLibrary from './components/ultra-admin/UltraLibrary';
import UltraWhatsAppQuotas from './components/ultra-admin/UltraWhatsAppQuotas';
import UltraTestimonials from './components/ultra-admin/UltraTestimonials';
import OnboardingFullPage from './components/OnboardingFullPage';
import SoloOnboardingPage from './components/SoloOnboardingPage';

import UltraCollections from './components/ultra-admin/UltraCollections';
import UltraPlatformRules from './components/ultra-admin/UltraPlatformRulesNew';
import UltraTermsOfService from './components/ultra-admin/UltraTermsOfService';
import ComingSoonPage from './components/ultra-admin/ComingSoonPage';
import SubscriptionPlans from './components/SubscriptionPlans';
import { 
  UsersIcon, 
  CreditCardIcon, 
  BarChart3Icon, 
  ToggleLeftIcon, 
  FileTextIcon, 
  SettingsIcon 
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Axios interceptor for token
axios.interceptors.request.use((config) => {
  // Check if this is an Ultra Admin API call
  const isUltraAdminAPI = config.url?.includes('/ultra-admin/');
  
  if (isUltraAdminAPI) {
    const ultraToken = localStorage.getItem('ultra_admin_token');
    if (ultraToken) {
      config.headers.Authorization = `Bearer ${ultraToken}`;
    }
  } else {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  return config;
});

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Don't redirect on login attempts - let the login page handle the errors
      const isLoginAttempt = error.config.url?.includes('/auth/login');
      // Onboarding sırasında hiçbir 401 hatası kullanıcıyı otomatik olarak
      // /login'e atmasın. Kullanıcı onboarding formunu doldururken alt-akış
      // API'larından (currencies, cities, invite, roles vb.) gelen tek bir
      // 401, tüm ilerlemesini silecek şekilde token'ı temizler ve sayfa
      // değiştirir. Bunun yerine promise reject edilir; onboarding kodu
      // hatayı kendisi yakalar. Gerçek anlamda token bitişi durumu ise
      // /auth/me çağrısıyla (checkAuth) tespit edilir.
      const isOnOnboarding = typeof window !== 'undefined' &&
        window.location.pathname.startsWith('/onboarding');

      if (!isLoginAttempt && !isOnOnboarding) {
        // Check if this is an Ultra Admin API call
        const isUltraAdminAPI = error.config.url?.includes('/ultra-admin/');
        
        if (isUltraAdminAPI) {
          localStorage.removeItem('ultra_admin_token');
          localStorage.removeItem('ultra_admin_user');
          window.location.href = '/ultra-admin';
        } else {
          localStorage.removeItem('token');
          window.location.href = '/login';
        }
      }
    }
    return Promise.reject(error);
  }
);

// Auth Context
const AuthContext = React.createContext();

export const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mustChangePassword, setMustChangePassword] = useState(false);

  const checkAuth = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      return { success: false };
    }

    try {
      const response = await axios.get(`${API}/auth/me`);
      // Backend returns {user, company, role, trial_info, plan_slug, plan_features}
      // We need to flatten this for backward compatibility
      const userData = {
        ...response.data.user,
        company: response.data.company,
        role: response.data.role,
        trial_info: response.data.trial_info,
        plan_slug: response.data.plan_slug,
        plan_features: response.data.plan_features || [],
        branding: response.data.branding || null,
        advance_mode: response.data.advance_mode || 'advanced'
      };
      setUser(userData);
      return { success: true };
    } catch (error) {
      // Only remove token on authentication errors (401/403)
      if (error.response?.status === 401 || error.response?.status === 403) {
        localStorage.removeItem('token');
        // Return error message if available
        const errorMessage = error.response?.data?.detail || 'Kimlik doğrulama başarısız';
        return { success: false, error: errorMessage };
      }
      return { success: false };
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkAuth();
  }, []);

  // Apply tenant-level branding (custom_branding feature) as CSS variables
  // whenever user.branding changes. This lets any component read
  // var(--brand-primary) / var(--brand-secondary) / var(--brand-accent) to
  // theme itself with the company's colors. When branding is active we also
  // add `brand-themed` to <body> so App.css can scope overrides to only
  // apply for tenants who set up branding (defaults stay teal/cyan for the
  // long tail of Solo/Starter accounts).
  useEffect(() => {
    const b = user?.branding;
    const root = document.documentElement;
    // Only apply tenant theming when the company has *explicitly* saved
    // branding (has_custom_branding=true). Otherwise the API returns default
    // colors, which would inadvertently theme every non-branded tenant.
    const hasBranding = b && b.has_custom_branding && (b.primary_color || b.secondary_color || b.accent_color);
    if (hasBranding) {
      if (b.primary_color) root.style.setProperty('--brand-primary', b.primary_color);
      if (b.secondary_color) root.style.setProperty('--brand-secondary', b.secondary_color);
      if (b.accent_color) root.style.setProperty('--brand-accent', b.accent_color);
      document.body.classList.add('brand-themed');
    } else {
      root.style.removeProperty('--brand-primary');
      root.style.removeProperty('--brand-secondary');
      root.style.removeProperty('--brand-accent');
      document.body.classList.remove('brand-themed');
    }
  }, [
    user?.branding?.has_custom_branding,
    user?.branding?.primary_color,
    user?.branding?.secondary_color,
    user?.branding?.accent_color,
  ]);

  const login = async (email, password, rememberMe = false) => {
    try {
      console.log('🔐 Attempting login for:', email, 'rememberMe:', rememberMe);
      const response = await axios.post(`${API}/auth/login`, {
        email,
        password,
        remember_me: !!rememberMe,
      });
      console.log('🔐 Login API response:', response.data);
      
      localStorage.setItem('token', response.data.access_token);
      localStorage.setItem('access_token', response.data.access_token);
      // Save refresh token so the axios interceptor can auto-refresh
      // expired access tokens instead of forcing the user back to /login.
      if (response.data.refresh_token) {
        localStorage.setItem('refresh_token', response.data.refresh_token);
      }
      // Clear any stale impersonation state from a previous Ultra Admin session
      localStorage.removeItem('impersonation');
      localStorage.removeItem('ultra_admin_token');
      localStorage.removeItem('ultra_admin_user');
      console.log('🔐 Token saved to localStorage');
      
      // Check if password must be changed
      if (response.data.must_change_password) {
        console.log('🔐 Setting mustChangePassword to TRUE');
        setMustChangePassword(true);
      }
      
      // Wait for checkAuth to complete and set user state
      console.log('🔐 Calling checkAuth...');
      const authResult = await checkAuth();
      console.log('🔐 checkAuth result:', authResult);
      
      if (!authResult.success) {
        // If checkAuth failed, return the error message
        console.error('❌ checkAuth failed:', authResult.error);
        if (authResult.error) {
          return {
            success: false,
            error: {
              message: authResult.error,
              field: 'general',
              type: 'auth_error'
            }
          };
        }
        throw new Error('Authentication verification failed');
      }
      
      console.log('✅ Login successful!');
      return {
        success: true,
        must_change_password: response.data.must_change_password,
        trial_expired: response.data.trial_expired,
        redirect_to: response.data.redirect_to
      };
    } catch (error) {
      console.error('❌ Login error:', error);
      console.error('❌ Error response:', error.response);
      const errorData = error.response?.data?.detail;
      
      // If error is structured (new format)
      if (errorData && typeof errorData === 'object') {
        return {
          success: false,
          error: {
            message: errorData.message,
            field: errorData.field,
            type: errorData.type
          }
        };
      }
      
      // If error is string (old format)
      const errorMessage = typeof errorData === 'string' ? errorData : 'Giriş başarısız';
      
      // Try to determine field from error message
      if (errorMessage.toLowerCase().includes('email') || errorMessage.toLowerCase().includes('e-posta')) {
        return {
          success: false,
          error: {
            message: errorMessage,
            field: 'email',
            type: 'email_error'
          }
        };
      } else if (errorMessage.toLowerCase().includes('password') || errorMessage.toLowerCase().includes('şifre')) {
        return {
          success: false,
          error: {
            message: errorMessage,
            field: 'password',
            type: 'password_error'
          }
        };
      }
      
      return {
        success: false,
        error: {
          message: errorMessage,
          field: 'general',
          type: 'general_error'
        }
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('impersonation');
    localStorage.removeItem('user');
    setUser(null);
  };

  const registerCompany = async (data) => {
    try {
      const response = await axios.post(`${API}/auth/register-company`, data);
      
      // Check if payment is required (paid plans)
      if (response.data.status === 'payment_required') {
        // Paid plan - need to create Stripe checkout session
        const pendingRegistrationId = response.data.pending_registration_id;
        
        // Create Stripe checkout session
        const checkoutResponse = await axios.post(
          `${API}/payments/checkout/session-for-registration?pending_registration_id=${pendingRegistrationId}`
        );
        
        // Redirect to Stripe checkout
        if (checkoutResponse.data.checkout_url) {
          window.location.href = checkoutResponse.data.checkout_url;
          return { status: 'redirecting_to_payment' };
        }
      }
      
      // Free trial - direct access with token
      if (response.data.access_token) {
        localStorage.setItem('token', response.data.access_token);
        
        // Wait for checkAuth to complete and verify user is set
        const authSuccess = await checkAuth();
        
        if (!authSuccess) {
          throw new Error('Kimlik doğrulama başarısız oldu. Lütfen giriş yapmayı deneyin.');
        }
        
        return true;
      }
      
      return response.data;
    } catch (error) {
      // Don't show toast here, throw error so RegisterPage can handle it
      throw error;
    }
  };

  const value = {
    user,
    token: localStorage.getItem('token'),
    login,
    logout,
    registerCompany,
    loading,
    checkAuth,
    mustChangePassword,
    setMustChangePassword
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Protected Route Component
const ProtectedRoute = ({ children, allowExpired = false }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  if (!user) return <Navigate to="/login" />;

  // If trial has expired and route does not allow expired users, force them to /subscription
  if (!allowExpired && user.trial_info?.is_expired) {
    return <Navigate to="/subscription" replace />;
  }

  return children;
};

// Password Change Modal Wrapper
const PasswordChangeModalWrapper = () => {
  const { mustChangePassword, setMustChangePassword } = useAuth();
  
  // Debug log
  React.useEffect(() => {
    console.log('🔐 PasswordChangeModalWrapper - mustChangePassword:', mustChangePassword);
  }, [mustChangePassword]);
  
  const handlePasswordChanged = () => {
    console.log('✅ Password changed, closing modal');
    setMustChangePassword(false);
  };
  
  return (
    <ChangePasswordModal 
      isOpen={mustChangePassword} 
      onPasswordChanged={handlePasswordChanged}
    />
  );
};

// Onboarding Wrapper Component
const OnboardingWrapper = () => {
  const { user } = useAuth();

  // Solo plan users get a minimal 2-step onboarding (skip company/team setup).
  const plan = user?.company?.subscription_plan || user?.company?.selected_plan;
  if (plan === 'solo') {
    return <SoloOnboardingPage user={user} />;
  }

  return (
    <OnboardingFullPage 
      user={user} 
      onComplete={() => {}} 
    />
  );
};

function App() {
  // Initialize theme on app start
  useEffect(() => {
    const initializeTheme = () => {
      const savedTheme = localStorage.getItem('theme') || 'light';
      const root = document.documentElement;
      
      root.classList.remove('dark', 'light');
      
      if (savedTheme === 'dark') {
        root.classList.add('dark');
      } else if (savedTheme === 'light') {
        root.classList.add('light');
      } else if (savedTheme === 'system') {
        const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
        root.classList.add(prefersDark ? 'dark' : 'light');
      }
    };
    
    initializeTheme();
  }, []);

  return (
    <PlatformSettingsProvider>
      <AuthProvider>
        <BrowserRouter>
          <PasswordChangeModalWrapper />
          <div className="App">
            <Routes>
              <Route path="/" element={<LandingPage />} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/register-company" element={<RegisterPage />} />
              <Route path="/register-user" element={<UserRegistration />} />
              <Route path="/email-verification-pending" element={<EmailVerificationPending />} />
              <Route path="/verify-email/:token" element={<EmailVerified />} />
              <Route path="/verify-email" element={<VerifyEmail />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/invite-expired" element={<InviteExpired />} />
              <Route path="/invite-cancelled" element={<InviteCancelled />} />
              <Route path="/share/audit/:shareId" element={<AuditShareLanding />} />
              
              {/* Payment Success Page */}
              <Route path="/payment-success" element={<PaymentSuccess />} />
              
              {/* Subscription & Payment Routes */}
              <Route
                path="/subscription"
                element={
                  <ProtectedRoute allowExpired>
                    <SubscriptionPlans />
                  </ProtectedRoute>
                }
              />

              {/* Onboarding Route */}
              <Route 
                path="/onboarding" 
                element={
                  <ProtectedRoute>
                    <OnboardingWrapper />
                  </ProtectedRoute>
                } 
              />

              {/* Ultra Admin Routes */}
              <Route path="/ultra-admin" element={<UltraAdminLogin />} />
              <Route path="/ultra-admin/*" element={<UltraAdminLayout />}>
                <Route path="dashboard" element={<UltraDashboard />} />
                <Route path="tenants" element={<UltraTenantManagement />} />
                <Route path="users" element={<UltraUserManagement />} />
                <Route path="profile" element={<UltraAdminProfile />} />
                <Route path="billing" element={<UltraBilling />} />
                <Route path="promo-codes" element={<UltraPromoCodes />} />
                <Route path="analytics" element={<UltraAnalytics />} />
                <Route path="features" element={<UltraFeatureManagement />} />
                <Route path="plan-comparison" element={<UltraPlanComparison />} />
                <Route path="storage-usage" element={<UltraStorageUsage />} />
                <Route path="roadmap" element={<UltraRoadmap />} />
                <Route path="audit" element={<UltraAuditLogs />} />
                <Route path="impersonation-logs" element={<ImpersonationLogs />} />
                <Route path="settings" element={<UltraPlatformSettings />} />
                <Route path="currencies" element={<CurrencyLibrary />} />
                <Route path="collections" element={<UltraCollections />} />
                <Route path="rules" element={<UltraPlatformRules />} />
                <Route path="library" element={<UltraLibrary />} />
                <Route path="whatsapp-quotas" element={<UltraWhatsAppQuotas />} />
                <Route path="terms" element={<UltraTermsOfService />} />
              </Route>
              
              <Route
                path="/dashboard/*"
                element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                }
              />
            </Routes>
            <Toaster position="bottom-right" richColors closeButton />
          </div>
        </BrowserRouter>
      </AuthProvider>
    </PlatformSettingsProvider>
  );
}

export default App;